'use client'

import React from 'react'
import type { Media } from '@/payload-types'
import { ToggleButton } from '@/components/ui/toggle-button'
import Image from 'next/image'
import { getMediaUrl } from '@/utilities/getMediaUrl'
import { CMSLink } from '@/components/Link'
import type { LinkFieldType } from '@/fields/link'
import { ShareIcon } from '@/components/icons/ShareIcon'

type CardStyleStandardProps = {
  title: string
  backgroundImage?: Media | string | null
  description?: string
  cta?: LinkFieldType
  isExpanded?: boolean
  onToggle?: () => void
  cardIndex?: number
}

export const CardStyleStandard: React.FC<CardStyleStandardProps> = ({
  title,
  backgroundImage,
  description,
  cta,
  isExpanded = false,
  onToggle,
  cardIndex: _cardIndex
}) => {
  // Check if CTA is valid (Payload returns empty object {} for unfilled link fields)
  const hasValidCta = cta && (cta.url || cta.reference)
  const { appearance: _ctaAppearance, ...ctaLinkProps } = cta || {}

  // Get background image URL and metadata
  let backgroundUrl: string | null = null
  let backgroundAlt = ''

  if (backgroundImage && typeof backgroundImage === 'object') {
    backgroundUrl = getMediaUrl(backgroundImage.url, backgroundImage.updatedAt)
    backgroundAlt = backgroundImage.alt || title
  } else if (typeof backgroundImage === 'string') {
    backgroundUrl = backgroundImage
  }

  return (
    <div className="w-full lg:flex-1">
      {/* Mobile: Accordion behavior, Desktop: Always show full card */}
      {isExpanded ? (
        // Mobile: Expanded Card (230px height), Desktop: Full card (305px height)
        hasValidCta ? (
          <CMSLink {...ctaLinkProps} label={undefined} appearance="inline" className="block">
            <div className="group box-border content-stretch flex gap-2.5 items-start justify-start p-6 relative rounded-[16px] shrink-0 w-full h-[230px] overflow-hidden lg:h-[305px] lg:flex-col lg:items-end lg:justify-between lg:transition-all lg:duration-300 lg:ease-in-out lg:hover:shadow-lg isolate bg-brand-dark">
              {/* Background Image with Gradient Overlay */}
              {backgroundUrl && (
                <div className="absolute inset-0 z-0">
                  <Image
                    src={backgroundUrl}
                    alt={backgroundAlt}
                    fill
                    className="object-cover"
                    sizes="(max-width: 1024px) 100vw, 33vw"
                  />
                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-b from-[rgba(7,7,7,0.5)] to-[rgba(196,196,196,0.1)]" />
                </div>
              )}
              <div className="content-stretch flex flex-col items-end justify-between relative z-10 shrink-0 w-full h-full">
                {/* Mobile: Title and toggle inline at top, description at bottom */}
                <div className="flex lg:hidden flex-col justify-between h-full w-full">
                  <div className="flex items-start justify-between gap-2 w-full">
                    <div className="display-x-small text-white flex-1 min-w-0">
                      <p className="mb-0">{title}</p>
                    </div>
                    <div className="relative shrink-0 size-[45px]">
                      <ToggleButton
                        isExpanded={isExpanded}
                        onClick={onToggle}
                        selectedColor="white"
                        unselectedColor="white"
                      />
                    </div>
                  </div>
                  {/* Mobile description at bottom with slide effect */}
                  {description && (
                    <div className="body-copy-medium text-white w-full overflow-hidden">
                      <div
                        className="animate-[slideUp_300ms_ease-out_100ms_forwards]"
                        style={{
                          opacity: 0,
                          transform: 'translateY(-8px)',
                        }}
                      >
                        <p className="mb-0">{description}</p>
                      </div>
                    </div>
                  )}
                </div>
                {/* Desktop: Just the toggle at top right */}
                <div className="relative shrink-0 size-[45px] hidden lg:block">
                  <ToggleButton
                    isExpanded={false}
                    selectedColor="black"
                    unselectedColor="lime"
                  />
                </div>

                {/* Desktop: Title and description at bottom */}
                <div className="hidden lg:flex content-stretch flex-col gap-4 items-start justify-start relative shrink-0 w-full">
                  <div className="display-xsmall text-white w-full">
                    <p className="mb-0 whitespace-pre">{title}</p>
                  </div>
                  {description && (
                    <div className="body-medium text-white w-full">
                      <p className="mb-0">{description}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </CMSLink>
        ) : (
          <div className="group box-border content-stretch flex gap-2.5 items-start justify-start p-6 relative rounded-[16px] shrink-0 w-full h-[230px] overflow-hidden lg:h-[305px] lg:flex-col lg:items-end lg:justify-between lg:transition-all lg:duration-300 lg:ease-in-out lg:hover:shadow-lg isolate bg-brand-dark">
            {/* Background Image with Gradient Overlay */}
            {backgroundUrl && (
              <div className="absolute inset-0 z-0">
                <Image
                  src={backgroundUrl}
                  alt={backgroundAlt}
                  fill
                  className="object-cover"
                  sizes="(max-width: 1024px) 100vw, 33vw"
                />
                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-b from-[rgba(7,7,7,0.5)] to-[rgba(196,196,196,0.1)]" />
              </div>
            )}
            <div className="content-stretch flex flex-col items-end justify-between relative z-10 shrink-0 w-full h-full">
              {/* Mobile: Title and toggle inline at top, description at bottom */}
              <div className="flex lg:hidden flex-col justify-between h-full w-full">
                <div className="flex items-start justify-between gap-2 w-full">
                  <div className="display-x-small text-white flex-1 min-w-0">
                    <p className="mb-0">{title}</p>
                  </div>
                  <div className="relative shrink-0 size-[45px]">
                    <ToggleButton
                      isExpanded={isExpanded}
                      onClick={onToggle}
                      selectedColor="white"
                      unselectedColor="white"
                    />
                  </div>
                </div>
                {/* Mobile description at bottom with slide effect */}
                {description && (
                  <div className="body-copy-medium text-white w-full overflow-hidden">
                    <div
                      className="animate-[slideUp_300ms_ease-out_100ms_forwards]"
                      style={{
                        opacity: 0,
                        transform: 'translateY(-8px)',
                      }}
                    >
                      <p className="mb-0">{description}</p>
                    </div>
                  </div>
                )}
              </div>
              {/* Desktop: Just the toggle at top right */}
              <div className="relative shrink-0 size-[45px] hidden lg:block">
                <ToggleButton
                  isExpanded={false}
                  selectedColor="black"
                  unselectedColor="lime"
                />
              </div>

              {/* Desktop: Title and description at bottom */}
              <div className="hidden lg:flex content-stretch flex-col gap-4 items-start justify-start relative shrink-0 w-full">
                <div className="display-xsmall text-white w-full">
                  <p className="mb-0 whitespace-pre">{title}</p>
                </div>
                {description && (
                  <div className="body-medium text-white w-full">
                    <p className="mb-0">{description}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )
      ) : (
        // Mobile: Collapsed Card (80px height), Desktop: Also show full card (305px height)
        hasValidCta ? (
          <CMSLink {...ctaLinkProps} label={undefined} appearance="inline" className="hidden lg:block">
            <div className="group box-border content-stretch flex gap-2.5 items-center justify-start p-6 relative rounded-[16px] shrink-0 w-full h-20 lg:h-[305px] lg:flex-col lg:items-end lg:justify-start lg:p-0 lg:transition-all lg:duration-300 lg:ease-in-out lg:hover:shadow-lg overflow-hidden isolate bg-brand-dark">
              {/* Desktop Background Image Overlay - only shows on lg+ */}
              {backgroundUrl && (
                <div className="hidden lg:block absolute inset-0 transition-opacity duration-300 group-hover:opacity-0 z-0">
                  <Image
                    src={backgroundUrl}
                    alt={backgroundAlt}
                    fill
                    className="object-cover"
                    sizes="(max-width: 1024px) 100vw, 33vw"
                  />
                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-b from-[rgba(7,7,7,0.5)] to-[rgba(196,196,196,0.1)]" />
                </div>
              )}

              {/* Desktop Layout: Full card content */}
              <div className="hidden lg:flex lg:flex-col lg:justify-between lg:relative lg:z-10 lg:shrink-0 lg:w-full lg:h-full lg:p-6">
                {/* Top row: Title on left, icon on right */}
                <div className="flex items-start justify-between gap-2 w-full">
                  <div className="subheading-large text-white group-hover:text-black transition-colors flex-1 min-w-0">
                    <p className="mb-0">{title}</p>
                  </div>
                  {/* Toggle button - only visible on hover with scale animation */}
                  <div className="relative shrink-0 size-[45px] opacity-0 scale-0 group-hover:opacity-100 group-hover:scale-100 transition-all duration-300 ease-out">
                    <div className="relative rounded-full size-[45px] bg-black">
                      <div className="absolute inset-0 flex items-center justify-center">
                        <ShareIcon className="size-[22px]" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Description at bottom - slides up like ExpandingRow effect */}
                {description && (
                  <div className="body-medium text-black/70 w-full overflow-hidden">
                    <div
                      className="opacity-0 group-hover:opacity-100 transition-all duration-300 ease-out"
                      style={{ transitionDelay: '100ms' }}
                    >
                      <p
                        className="mb-0 transform translate-y-[-8px] group-hover:translate-y-0 transition-transform duration-300 ease-out"
                        style={{ transitionDelay: '100ms' }}
                      >{description}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </CMSLink>
        ) : null
      )}
      {/* Mobile collapsed state without link (shown for both cta and no-cta cases) */}
      {!isExpanded && (
        <div
          className="lg:hidden box-border flex items-center justify-between gap-2 p-6 relative rounded-[16px] w-full min-h-20 overflow-hidden isolate cursor-pointer bg-brand-dark"
          onClick={onToggle}
        >
          {/* Mobile Layout: Title and toggle side by side */}
          <div className="display-x-small text-black flex-1 min-w-0">
            <p className="mb-0">{title}</p>
          </div>
          <div className="relative shrink-0 size-[45px]">
            <ToggleButton
              isExpanded={isExpanded}
              onClick={onToggle}
              selectedColor="black"
              unselectedColor="black"
            />
          </div>
        </div>
      )}
      {/* Desktop non-link version (only shown when no valid cta) */}
      {!isExpanded && !hasValidCta && (
        <div className="hidden lg:block group box-border content-stretch flex gap-2.5 items-center justify-start p-6 relative rounded-[16px] shrink-0 w-full h-20 lg:h-[305px] lg:flex-col lg:items-end lg:justify-start lg:p-0 lg:transition-all lg:duration-300 lg:ease-in-out lg:hover:shadow-lg overflow-hidden isolate bg-brand-dark">
          {/* Desktop Background Image Overlay - only shows on lg+ */}
          {backgroundUrl && (
            <div className="hidden lg:block absolute inset-0 transition-opacity duration-300 group-hover:opacity-0 z-0">
              <Image
                src={backgroundUrl}
                alt={backgroundAlt}
                fill
                className="object-cover"
                sizes="(max-width: 1024px) 100vw, 33vw"
              />
              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-b from-[rgba(7,7,7,0.5)] to-[rgba(196,196,196,0.1)]" />
            </div>
          )}

          {/* Desktop Layout: Full card content (no arrow since no link) */}
          <div className="hidden lg:flex lg:flex-col lg:justify-between lg:relative lg:z-10 lg:shrink-0 lg:w-full lg:h-full lg:p-6">
            {/* Top row: Title only (no arrow since no link) */}
            <div className="flex items-start justify-between gap-3 w-full">
              <div className="subheading-large text-white group-hover:text-black transition-colors flex-1 min-w-0">
                <p className="mb-0">{title}</p>
              </div>
            </div>

            {/* Description at bottom - slides up like ExpandingRow effect */}
            {description && (
              <div className="body-medium text-black/70 w-full overflow-hidden">
                <div
                  className="opacity-0 group-hover:opacity-100 transition-all duration-300 ease-out"
                  style={{ transitionDelay: '100ms' }}
                >
                  <p
                    className="mb-0 transform translate-y-[-8px] group-hover:translate-y-0 transition-transform duration-300 ease-out"
                    style={{ transitionDelay: '100ms' }}
                  >{description}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
